import { useEffect } from "react";
import { useLanguage } from "@/lib/i18n";
import { Layout } from "@/components/layout/Layout";
import type { BlogPost } from "@/data/content-types";
import {
  applyPageSeo,
  appStoreUrlFor,
  articleLd,
  breadcrumbLd,
  faqLd,
  upsertJsonLd,
  upsertMeta,
  SITE_URL,
} from "@/lib/seo";
import {
  Breadcrumbs,
  ComparisonGrid,
  CtaBand,
  FaqAccordion,
  MatrixGrid,
  Prose,
  RelatedLinks,
  Sections,
} from "./blocks";

const ui = {
  fr: { home: "Accueil", blog: "Blog", free: "Gratuit à télécharger. Disponible sur iPhone et Android." },
  en: { home: "Home", blog: "Blog", free: "Free to download. Available on iPhone and Android." },
};

export function ArticlePage({ post }: { post: BlogPost }) {
  const { language } = useLanguage();
  const c = post[language];
  const path = language === "fr" ? post.frPath : post.enPath;
  const url = `${SITE_URL}${path}`;
  const t = ui[language];
  const blogPath = language === "fr" ? "/blog" : "/en/blog";
  const image = post.image.startsWith("http") ? post.image : `${SITE_URL}${post.image}`;

  useEffect(() => {
    applyPageSeo({
      lang: language,
      title: c.metaTitle,
      description: c.metaDescription,
      keywords: c.keywords,
      path,
      frPath: post.frPath,
      enPath: post.enPath,
      ogImage: image,
      ogType: "article",
    });

    upsertMeta("property", "article:published_time", post.datePublished);
    upsertMeta("property", "article:modified_time", post.dateModified);
    upsertMeta("property", "article:section", c.category);

    upsertJsonLd(
      "article",
      articleLd({
        lang: language,
        url,
        headline: c.h1,
        description: c.metaDescription,
        image,
        section: c.category,
        datePublished: post.datePublished,
        dateModified: post.dateModified,
        keywords: c.keywords,
      }),
    );
    upsertJsonLd(
      "breadcrumb",
      breadcrumbLd([
        { name: t.home, url: language === "fr" ? `${SITE_URL}/` : `${SITE_URL}/en` },
        { name: t.blog, url: `${SITE_URL}${blogPath}` },
        { name: c.breadcrumb, url },
      ]),
    );
    upsertJsonLd("faq", faqLd(c.faq.map((f) => ({ q: f.q, a: f.a })), `${url}#faq`));
  }, [language, c, path, url, post, t.home, t.blog, blogPath, image]);

  return (
    <Layout>
      <article className="relative">
        <section className="relative overflow-hidden noise-bg pt-28 pb-10 md:pt-36 md:pb-12">
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 70% 50% at 50% 0%, hsl(var(--primary) / 0.10) 0%, transparent 70%)",
            }}
          />
          <div className="relative z-10 mx-auto max-w-3xl px-4 sm:px-6">
            <Breadcrumbs
              trail={[
                { label: t.home, href: language === "fr" ? "/" : "/en" },
                { label: t.blog, href: blogPath },
                { label: c.breadcrumb },
              ]}
            />
            <div className="flex flex-wrap items-center gap-3 text-xs font-medium text-muted-foreground mb-4">
              <span className="rounded-full bg-primary/10 px-3 py-1 font-semibold text-primary">
                {c.category}
              </span>
              <span>{c.date}</span>
              <span aria-hidden>·</span>
              <span>{c.readingTime}</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6">
              {c.h1}
            </h1>
            <div className="max-w-2xl text-lg">
              <Prose paragraphs={c.intro} />
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-3xl px-4 sm:px-6 pb-16 space-y-14 md:space-y-16">
          <Sections sections={c.sections} />
          {c.matrix ? <MatrixGrid data={c.matrix} /> : null}
          {c.comparison ? <ComparisonGrid data={c.comparison} /> : null}
          <FaqAccordion title={c.faqTitle} items={c.faq} />
          <RelatedLinks title={c.relatedTitle} links={c.related} />
          <CtaBand
            title={c.ctaTitle}
            body={c.ctaBody}
            appStoreUrl={appStoreUrlFor(language)}
            freeLabel={t.free}
          />
        </div>
      </article>
    </Layout>
  );
}
